export const RANKING_SIZE = 10;
export const DEFAULT_NAME = "NO NAME";
export const MAX_NAME_LENGTH = 16;

export interface RankingEntry {
  name: string;
  score: number;
}

function normalizeNameText(name: string): string {
  return name.replace(/[\u0000-\u001f\u007f]/g, " ").replace(/\s+/g, " ").trim();
}

export function nameCharacterCount(name: string): number {
  return Array.from(normalizeNameText(name)).length;
}

export function isNameTooLong(name: string): boolean {
  return nameCharacterCount(name) > MAX_NAME_LENGTH;
}

export function sanitizeName(name: string): string {
  const normalized = Array.from(normalizeNameText(name)).slice(0, MAX_NAME_LENGTH).join("");
  return normalized.length > 0 ? normalized : DEFAULT_NAME;
}

export function createDefaultRanking(): RankingEntry[] {
  return Array.from({ length: RANKING_SIZE }, (_, index) => ({
    name: DEFAULT_NAME,
    score: 10000 - index * 1000
  }));
}

export function normalizeRanking(entries: RankingEntry[]): RankingEntry[] {
  const normalized = entries
    .map((entry) => ({
      name: sanitizeName(entry.name),
      score: Number.isFinite(entry.score) ? Math.max(0, Math.floor(entry.score)) : 0
    }))
    .sort((a, b) => b.score - a.score);

  while (normalized.length < RANKING_SIZE) {
    normalized.push(createDefaultRanking()[normalized.length]);
  }

  return normalized.slice(0, RANKING_SIZE);
}

export function isRankedScore(score: number, ranking: RankingEntry[]): boolean {
  const normalized = normalizeRanking(ranking);
  return Math.floor(score) > normalized[normalized.length - 1].score;
}

export function rankForScore(score: number, ranking: RankingEntry[]): number {
  const normalized = normalizeRanking(ranking);
  const normalizedScore = Math.max(0, Math.floor(score));
  const insertAt = normalized.findIndex((candidate) => normalizedScore > candidate.score);
  return insertAt === -1 ? normalized.length + 1 : insertAt + 1;
}

export function insertRanking(score: number, name: string, ranking: RankingEntry[]): RankingEntry[] {
  const entry = { name: sanitizeName(name), score: Math.max(0, Math.floor(score)) };
  const next = normalizeRanking(ranking);
  let insertAt = next.findIndex((candidate) => entry.score > candidate.score);
  if (insertAt === -1) {
    insertAt = next.length;
  }
  next.splice(insertAt, 0, entry);
  return next.slice(0, RANKING_SIZE);
}

export function parseLegacyIniRanking(text: string): RankingEntry[] {
  const names = new Map<number, string>();
  const scores = new Map<number, number>();

  for (const line of text.split(/\r?\n/)) {
    const nameMatch = /^Name\s+(\d+)\s*=(.*)$/i.exec(line.trim());
    if (nameMatch) {
      names.set(Number(nameMatch[1]), nameMatch[2]);
      continue;
    }

    const scoreMatch = /^Score\s+(\d+)\s*=\s*(-?\d+)/i.exec(line.trim());
    if (scoreMatch) {
      scores.set(Number(scoreMatch[1]), Number(scoreMatch[2]));
    }
  }

  const entries: RankingEntry[] = [];
  for (let index = 1; index <= RANKING_SIZE; index += 1) {
    entries.push({
      name: names.get(index) ?? DEFAULT_NAME,
      score: scores.get(index) ?? createDefaultRanking()[index - 1].score
    });
  }
  return normalizeRanking(entries);
}

export function exportLegacyIniRanking(ranking: RankingEntry[]): string {
  const normalized = normalizeRanking(ranking);
  const lines = ["[Friel]"];
  normalized.forEach((entry, index) => {
    const oneBased = index + 1;
    lines.push(`Name ${oneBased}=${entry.name}`);
    lines.push(`Score ${oneBased}= ${entry.score}`);
  });
  return `${lines.join("\n")}\n`;
}
